import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import AxiosService from "../../services/AxiosHelper";
import { CURRENCY_SYMBOLS } from "../../utils/currency";

const baseURL = process.env.REACT_APP_BASEURL;

export const createBusiness = createAsyncThunk(
  "business/createBusiness",
  async (businessData, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "post",
        url: baseURL + "/business",
        data: businessData,
      });
      toast.success("Business created successfully!");
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to create business");
    }
  }
);

export const getBusinessesList = createAsyncThunk(
  "business/getBusinessesList",
  async (masterCustomerId, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        url: baseURL + `/business/master-customer/${masterCustomerId}`,
        method: "get",
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch businesses");
    }
  }
);
export const getIndividualBusiness = createAsyncThunk(
  "business/getIndividualBusiness",
  async (id, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "get",
        url: baseURL + `/business/${id}`,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch business details");
    }
  }
);

export const getBusinessById = createAsyncThunk(
  "business/getBusinessById",
  async (businessId, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "get",
        url: baseURL + `/business/${businessId}`,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error fetching details");
    }
  }
);

export const updatePaymentPolicy = createAsyncThunk(
  'business/updatePaymentPolicy',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "patch",
        url: baseURL + `/business/${id}/payment-policy`,
        data
      });
      const updatedData = response?.data || response;
      return updatedData.data || updatedData;
    } catch (err) {
      const message = err.response?.data?.message || err.message || "Update failed";
      return rejectWithValue(message);
    }
  }
);

export const updateBusinessInfo = createAsyncThunk(
  "business/updateBusinessInfo",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "put",
        url: baseURL + `/business/${id}`,
        data: data,
      });
      toast.success("Business information updated!");
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Update failed");
    }
  }
);

export const updateInvoiceSettings = createAsyncThunk(
  "business/updateInvoiceSettings",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "patch",
        url: baseURL + `/business/${id}/invoice-settings`,
        data: data,
      });
      toast.success("Invoice settings updated!");
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Update failed");
    }
  }
);

export const updateBusinessSettings = createAsyncThunk(
  "business/updateBusinessSettings",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "patch",
        url: baseURL + `/business/${id}/settings`,
        data: data,
      });
      toast.success("Regional and tax settings updated!");
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Update failed");
    }
  }
);

export const uploadBusinessLogo = createAsyncThunk(
  "business/uploadLogo",
  async ({ id, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      const response = await AxiosService.AxiostRequest({
        method: "post",
        url: baseURL + `/business/${id}/logo`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo updated!");
      return response.data.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Logo upload failed";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchCurrencyConfig = createAsyncThunk(
  "business/fetchCurrencyConfig",
  async (businessId, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "get",
        url: baseURL + `/business/${businessId}/currency-config`,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch currency config");
    }
  }
);

const businessSlice = createSlice({
  name: "business",
  initialState: {
    businessList: [],
    loading: false,
    individualBusiness: null,
    selectedBusiness: null,
    activeBusinessId: localStorage.getItem("activeBusinessId") || null,
    businessDetail: null,
    currencyContext: {
      currency: 'CAD',
      symbol: 'CA$',
      locale: 'en-CA',
      timezone: 'America/Toronto',
      taxByBusiness: { type: 'bypass', percentage: 0, label: '', appliesTo: 'all' }
    }
  },
  reducers: {
    setSelectedBusiness: (state, action) => {
      state.selectedBusiness = action.payload;
    },
    setActiveBusiness: (state, action) => {
      state.activeBusinessId = action.payload;
      localStorage.setItem("activeBusinessId", action.payload);
    },
    setCurrencyContext: (state, action) => {
      state.currencyContext = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase("auth/logIn/fulfilled", (state, action) => {
      state.activeBusinessId = action.payload?.user?.primaryBusinessId || null;
    });
    builder.addCase(createBusiness.fulfilled, (state, { payload }) => {
      state.businessList = [...state.businessList, payload];
      state.loading = false;
    });
    builder.addCase(getBusinessesList.fulfilled, (state, action) => {
      state.businessList = action.payload || [];
      state.loading = false;

      // Ensure a specific business is selected based on activeBusinessId or default to first
      if (state.businessList.length > 0) {
        const targetBusiness =
          state.businessList.find((b) => b._id === state.activeBusinessId) ||
          state.businessList[0];

        state.selectedBusiness = targetBusiness;
        if (targetBusiness?.jssettings) {
          state.currencyContext = { 
            ...state.currencyContext, 
            ...targetBusiness.jssettings,
            symbol: CURRENCY_SYMBOLS[targetBusiness.jssettings.currency] || '$',
            taxByBusiness: targetBusiness.taxByBusiness || state.currencyContext.taxByBusiness
          };
        }
        
        if (!state.activeBusinessId) {
          state.activeBusinessId = targetBusiness._id;
          localStorage.setItem("activeBusinessId", targetBusiness._id);
        }
      }
    });
    builder.addCase(getIndividualBusiness.fulfilled, (state, { payload }) => {
      state.individualBusiness = payload;
      state.businessDetail = payload;
      if (payload?.jssettings) {
        state.currencyContext = { 
          ...state.currencyContext, 
          ...payload.jssettings,
          symbol: CURRENCY_SYMBOLS[payload.jssettings.currency] || '$',
          taxByBusiness: payload.taxByBusiness || state.currencyContext.taxByBusiness
        };
      }
      state.loading = false;
    });
    builder.addCase(getBusinessById.fulfilled, (state, { payload }) => {
      state.individualBusiness = payload;
      state.businessDetail = payload;
      if (payload?.jssettings) {
        state.currencyContext = { 
          ...state.currencyContext, 
          ...payload.jssettings,
          symbol: CURRENCY_SYMBOLS[payload.jssettings.currency] || '$',
          taxByBusiness: payload.taxByBusiness || state.currencyContext.taxByBusiness
        };
      }
      state.loading = false;
    });
    builder.addCase(updatePaymentPolicy.fulfilled, (state, action) => {
      state.individualBusiness = action.payload;
      state.businessDetail = action.payload;
      state.loading = false;
    });
    builder.addCase(updateBusinessInfo.fulfilled, (state, action) => {
      state.individualBusiness = action.payload;
      state.businessDetail = action.payload;
      state.loading = false;
    });
    builder.addCase(updateInvoiceSettings.fulfilled, (state, action) => {
      state.individualBusiness = action.payload;
      state.businessDetail = action.payload;
      state.loading = false;
    });
    builder.addCase(updateBusinessSettings.fulfilled, (state, action) => {
      state.individualBusiness = action.payload;
      state.businessDetail = action.payload;
      state.loading = false;
    });
    builder.addCase(uploadBusinessLogo.fulfilled, (state, action) => {
      state.individualBusiness = action.payload;
      state.businessDetail = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchCurrencyConfig.fulfilled, (state, action) => {
      state.currencyContext = action.payload;
    });

    // General Matchers
    builder.addMatcher(
      (action) => action.type.startsWith("business/") && action.type.endsWith("/pending"),
      (state) => { state.loading = true; }
    );
    builder.addMatcher(
      (action) => action.type.startsWith("business/") && (action.type.endsWith("/fulfilled") || action.type.endsWith("/rejected")),
      (state, action) => {
        state.loading = false;

        if (action.type.endsWith("/fulfilled") && action.payload?.jssettings) {
          state.currencyContext = { 
            ...state.currencyContext, 
            ...action.payload.jssettings,
            symbol: CURRENCY_SYMBOLS[action.payload.jssettings.currency] || '$',
            taxByBusiness: action.payload.taxByBusiness || state.currencyContext.taxByBusiness
          };
        }

        if (action.type.endsWith("/rejected")) {
          toast.error(action.payload?.message || "Something went wrong");
        }
      }
    );
  },
});

export const { setSelectedBusiness, setActiveBusiness, setCurrencyContext } = businessSlice.actions;
export default businessSlice.reducer;
